import React, { useState } from 'react'
import { Modal, Pressable, StyleSheet, View } from 'react-native'
import { useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import type { LoungeRole } from '@ppwr/convex/lounges/shared'
import { DS } from '@/design-system'
import { translate } from '../../../i18n'
import type { LoungeMember as Member } from '../types'

type PendingAction = 'makeOwner' | 'makeCoOwner' | 'remove'

type MemberSettingsSheetProps = {
  visible: boolean
  member: Member | null
  loungeId: Id<'lounges'>
  callerUserId: string
  callerRole: LoungeRole
  onClose: () => void
}

export function MemberSettingsSheet({
  visible,
  member,
  loungeId,
  callerUserId,
  callerRole,
  onClose
}: MemberSettingsSheetProps) {
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)

  const promoteToOwner = useMutation(api.lounges.mutations.promoteToOwner)
  const updateMemberRole = useMutation(api.lounges.mutations.updateMemberRole)
  const removeMember = useMutation(api.lounges.mutations.removeMember)

  if (!member) return null

  const handleClose = () => {
    setPendingAction(null)
    onClose()
  }

  const handleConfirm = async () => {
    if (pendingAction === 'makeOwner') {
      await promoteToOwner({ loungeId, actorId: callerUserId, targetUserId: member.userId })
    } else if (pendingAction === 'makeCoOwner') {
      await updateMemberRole({ loungeId, actorId: callerUserId, targetUserId: member.userId, newRole: 'coowner' })
    } else if (pendingAction === 'remove') {
      await removeMember({ loungeId, actorId: callerUserId, targetUserId: member.userId })
    }
    handleClose()
  }

  const confirmTitle =
    pendingAction === 'makeOwner'
      ? translate('loungeLobbyScreen.makeOwnerConfirm')
      : pendingAction === 'makeCoOwner'
        ? translate('loungeLobbyScreen.makeCoOwnerConfirm')
        : translate('loungeLobbyScreen.removeMemberConfirm')

  const canRemove = callerRole === 'owner' || (callerRole === 'coowner' && member.role === 'member')

  return (
    <Modal transparent visible={visible} animationType="fade" onRequestClose={handleClose}>
      <View style={styles.container}>
        <Pressable style={StyleSheet.absoluteFill} onPress={handleClose} />
        <View style={styles.card}>
          {pendingAction ? (
            <>
              <DS.Text variant="Heading 3/Medium" style={[styles.title, styles.confirmTitle]}>
                {confirmTitle}
              </DS.Text>
              <View style={styles.confirmRow}>
                <Pressable
                  style={({ pressed }) => [styles.cancelButton, pressed && { opacity: 0.85 }]}
                  onPress={() => setPendingAction(null)}
                >
                  <DS.Text variant="Body Large/Medium" style={styles.cancelLabel}>
                    {translate('loungeLobbyScreen.confirmCancel')}
                  </DS.Text>
                </Pressable>
                <Pressable
                  style={({ pressed }) => [styles.confirmButton, pressed && { opacity: 0.85 }]}
                  onPress={handleConfirm}
                >
                  <DS.Text variant="Body Large/Medium" style={styles.confirmLabel}>
                    {translate('loungeLobbyScreen.confirmConfirm')}
                  </DS.Text>
                </Pressable>
              </View>
            </>
          ) : (
            <>
              <View style={styles.header}>
                <DS.Text variant="Heading 3/Medium" style={styles.title}>
                  {translate('loungeLobbyScreen.memberSettings')}
                </DS.Text>
                <Pressable onPress={handleClose} hitSlop={8} style={({ pressed }) => pressed && { opacity: 0.7 }}>
                  <DS.Icon name="Close" size={20} color="Global/White" />
                </Pressable>
              </View>

              {callerRole === 'owner' && member.role !== 'owner' && (
                <ActionButton
                  label={translate('loungeLobbyScreen.makeOwner')}
                  onPress={() => setPendingAction('makeOwner')}
                />
              )}
              {member.role === 'member' && (
                <ActionButton
                  label={translate('loungeLobbyScreen.makeCoOwner')}
                  onPress={() => setPendingAction('makeCoOwner')}
                />
              )}
              {canRemove && (
                <ActionButton
                  label={translate('loungeLobbyScreen.removeMember')}
                  onPress={() => setPendingAction('remove')}
                />
              )}
            </>
          )}
        </View>
      </View>
    </Modal>
  )
}

function ActionButton({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <Pressable style={({ pressed }) => [styles.actionButton, pressed && { opacity: 0.85 }]} onPress={onPress}>
      <DS.Text variant="Body Large/Medium" style={styles.actionLabel}>
        {label}
      </DS.Text>
    </Pressable>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)'
  },
  card: {
    width: '88%',
    backgroundColor: DS.palette['Global/Black'],
    borderRadius: 16,
    padding: 20,
    gap: 12
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4
  },
  title: {
    color: DS.palette['Global/White']
  },
  confirmTitle: {
    textAlign: 'center',
    marginBottom: 8
  },
  confirmRow: {
    flexDirection: 'row',
    gap: 12
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 16,
    alignItems: 'center'
  },
  cancelLabel: {
    color: DS.palette['Global/White']
  },
  confirmButton: {
    flex: 1,
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center'
  },
  confirmLabel: {
    color: DS.palette['Global/Black']
  },
  actionButton: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 20,
    alignItems: 'center'
  },
  actionLabel: {
    color: DS.palette['Global/Black']
  }
})
